// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: how loud the soundboard is for *you* (strand AJ,
// docs/strands/soundboard.md). The speaker button in the soundboard panel
// sets it, like Discord's.
//
// Stored on this device, not in the synced preferences: it belongs with
// the output volume and the output device, which are device settings too -
// the same account on a laptop and on a phone wants different values.

import AppStorage from '@app/features/platform/state/PersistentStorage';
import {makeAutoObservable} from 'mobx';

const STORAGE_KEY = 'parley_soundboard_volume';

export const SOUNDBOARD_VOLUME_MIN = 0;
export const SOUNDBOARD_VOLUME_MAX = 100;
const DEFAULT_VOLUME = 100;

function clamp(value: number): number {
	if (!Number.isFinite(value)) return DEFAULT_VOLUME;
	return Math.max(SOUNDBOARD_VOLUME_MIN, Math.min(SOUNDBOARD_VOLUME_MAX, Math.round(value)));
}

/** Nothing stored (or nonsense stored) means full volume, not silence. */
function parse(raw: string | null): number {
	if (raw == null || raw === '') return DEFAULT_VOLUME;
	return clamp(Number(raw));
}

class SoundboardVolumeStore {
	/** Percent, 0 to 100. */
	volume: number = DEFAULT_VOLUME;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.volume = parse(AppStorage.getItem(STORAGE_KEY));
		AppStorage.subscribe((event) => {
			// Another tab moved the slider.
			if (event.key === STORAGE_KEY && event.source === 'external') {
				this.volume = parse(event.newValue);
			}
		});
	}

	setVolume(value: number): void {
		const next = clamp(value);
		if (next === this.volume) return;
		this.volume = next;
		AppStorage.setItem(STORAGE_KEY, String(next));
	}

	/** The factor a sound is played with (0 to 1). */
	get gain(): number {
		return this.volume / 100;
	}
}

export default new SoundboardVolumeStore();
